import type { KeyboardEvent, MouseEvent } from 'react';
import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import { Tooltip } from '../../tooltip/tooltip';
import { getSelectText } from '../helper-functions/getSelectText';
import ChipsInManyRows from './chips-in-many-rows';
import ChipsInSingleRow from './chips-in-single-row';
import Clear from './clear';
import SelectArrowDown from './select-arrow-down';

interface ISelected {
  selectValue: string | string[];
  optionsMap: Record<string, string>;
  placeholder?: string;
  placeholderColor?: string;
  selectedVariant?: TypographyVariant;
  handleRemoveSelectValue: (value: string) => void;
  open?: boolean;
  onClear?: () => void;
  clearable: boolean;
  disabled: boolean;
  optionsTooltipMaxCharCount?: number;
  hasError?: boolean;
  closeButtonLabel?: string;
}
const Selected = ({
  selectValue,
  optionsMap,
  placeholder,
  placeholderColor,
  selectedVariant = TypographyVariant.bodyMd1,
  handleRemoveSelectValue,
  open = false,
  onClear,
  clearable,
  disabled,
  optionsTooltipMaxCharCount = 100,
  hasError = false,
  closeButtonLabel,
}: ISelected) => {
  const theme = useTheme();
  const handleClear = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    onClear?.();
  };
  if (!Array.isArray(selectValue)) {
    const selectedText = getSelectText(selectValue, optionsMap, placeholder);

    if (selectedText === placeholder) {
      return (
        <Wrapper>
          <PlaceholderTextWrapper
            $placeholderColor={
              disabled ? theme.palette.neutral[70] : placeholderColor
            }
            $selectedVariant={selectedVariant}
            $hasError={hasError}
            tabIndex={-1}
          >
            {placeholder}
          </PlaceholderTextWrapper>
          <SelectArrowDown
            disabled={disabled}
            isOpen={open}
            hasError={hasError}
          />
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <TextWrapper $selectedVariant={selectedVariant}>
          {selectedText?.length > optionsTooltipMaxCharCount ? (
            <Tooltip tooltipText={selectedText} tabIndex={-1}>
              {selectedText}
            </Tooltip>
          ) : (
            selectedText
          )}
        </TextWrapper>
        {clearable && Boolean(selectValue) && !disabled && (
          <Clear handleClear={handleClear} />
        )}
        <SelectArrowDown
          disabled={disabled}
          isOpen={open}
          hasError={hasError}
        />
      </Wrapper>
    );
  }

  const selectArrayValue = Array.isArray(selectValue) ? selectValue : [];

  if (selectArrayValue.length === 0) {
    return (
      <Wrapper>
        <PlaceholderTextWrapper
          $placeholderColor={placeholderColor}
          $selectedVariant={selectedVariant}
        >
          {placeholder}
        </PlaceholderTextWrapper>
        <SelectArrowDown
          disabled={disabled}
          isOpen={open}
          hasError={hasError}
        />
      </Wrapper>
    );
  }

  if (open) {
    return (
      <>
        <ChipsWrapper>
          <ChipsInManyRows
            selectValue={selectArrayValue}
            optionsMap={optionsMap}
            handleRemoveSelectValue={handleRemoveSelectValue}
            closeButtonLabel={closeButtonLabel}
            disabled={disabled}
          />
        </ChipsWrapper>
        <ArrowDownWrapper>
          <SelectArrowDown
            disabled={disabled}
            isOpen={open}
            hasError={hasError}
          />
        </ArrowDownWrapper>
      </>
    );
  }
  return (
    <>
      <ChipsWrapper>
        <ChipsInSingleRow
          selectValue={selectArrayValue}
          optionsMap={optionsMap}
          handleRemoveSelectValue={handleRemoveSelectValue}
          closeButtonLabel={closeButtonLabel}
          disabled={disabled}
        />
      </ChipsWrapper>
      <SelectArrowDown disabled={disabled} isOpen={open} />
    </>
  );
};

export default memo(Selected);

const TextWrapper = styled.div`
  ${({
    theme,
    $selectedVariant,
  }: {
    theme: ITheme;
    $selectedVariant: TypographyVariant;
  }) => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: ${theme.spacing(1)};
    width: 100%;
    text-align: left;
    ${theme.typography[$selectedVariant]};
    @media (max-width: ${theme.breakpoints.sm}) {
      // font size of 16px on mobile to avoid zoom
      font-size: 16px;
    }
  `}
`;

const PlaceholderTextWrapper = styled(TextWrapper)`
  ${({
    theme,
    $placeholderColor,
    $selectedVariant,
    $hasError,
  }: {
    theme: ITheme;
    $placeholderColor?: string;
    $selectedVariant: TypographyVariant;
    $hasError?: boolean;
  }) => css`
    ${theme.typography[$selectedVariant]};

    color: ${$hasError
      ? theme.palette.error.main
      : ($placeholderColor ?? theme.palette.neutral['70'])};

    div {
      color: ${$hasError
        ? theme.palette.error.main
        : ($placeholderColor ?? theme.palette.neutral['70'])};
    }
  `}
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ChipsWrapper = styled.div`
  ${({ theme }) => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    margin-right: ${theme.spacing(1)};
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
  `}
`;

const ArrowDownWrapper = styled.div`
  ${({ theme }) => css`
    height: calc(${theme?.input?.text?.height} - ${theme.spacing(2)});
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: flex-start;
  `}
`;
